import { useCallback, useState } from 'react';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import type { CoPilotTraceExportStatus } from '../../../utils/co-pilot-trace-export';
import {
  COPILOT_SESSION_EXPORT_FORMAT,
  type CoPilotSessionExportFormat,
} from './copilot-session-export-formats';
import {
  assertCoPilotSessionExportableContent,
  downloadCoPilotSessionExport,
} from './co-pilot-session-export';
import { useCoPilotSessionExportTargetId } from './useCoPilotSessionExportTargetId';

export function useCoPilotSessionExport({
  threadId,
  applicationId,
  messages,
  status = 'completed',
  chatTitle,
}: {
  threadId?: string;
  applicationId?: string;
  messages: TerraUIMessage[];
  status?: CoPilotTraceExportStatus;
  chatTitle?: string;
}) {
  const [exportingFormat, setExportingFormat] =
    useState<CoPilotSessionExportFormat | null>(null);
  const resolvedTargetId = useCoPilotSessionExportTargetId({
    threadId,
    targetId: applicationId,
  });

  const exportSession = useCallback(
    async (format: CoPilotSessionExportFormat) => {
      if (!threadId || !resolvedTargetId) {
        return;
      }

      assertCoPilotSessionExportableContent({ messages });

      setExportingFormat(format);
      try {
        await downloadCoPilotSessionExport({
          threadId,
          applicationId: resolvedTargetId,
          format,
          status,
          chatTitle,
        });
      } finally {
        setExportingFormat(null);
      }
    },
    [chatTitle, messages, resolvedTargetId, status, threadId],
  );

  return {
    exportSession,
    exportingFormat,
    isExporting: exportingFormat !== null,
    exportFormats: COPILOT_SESSION_EXPORT_FORMAT,
    canExport: Boolean(threadId && resolvedTargetId),
  };
}
